/**
 * Package session options: a tenant sells one package at several session
 * counts ("Training Package" × 4 / 8 / 12) as separate `booking_packages`
 * rows that share every customer-facing attribute except the count, price
 * and validity. Purchases and credits keep pointing at the concrete row;
 * these pure helpers only decide which rows display as one tile.
 * Client-safe (no server imports).
 */

export interface PackageSessionOptionKeySource {
  name: string;
  package_type: string;
  template_id?: string | null;
  template_ids?: readonly string[] | null;
  restrict_to_sport_id?: string | null;
  location_id?: string | null;
  tier?: string | null;
  slot_duration?: number | null;
  is_first_time_only?: boolean | null;
  require_attendance_within_validity?: boolean | null;
  fixed_starts_at?: string | null;
  fixed_ends_at?: string | null;
  status?: string | null;
  is_internal?: boolean | null;
  description?: string | null;
  description_alt?: string | null;
  features?: unknown;
  features_alt?: unknown;
  player_pricing?: unknown;
  bundle_id?: string | null;
  is_fixed_slot?: boolean | null;
  class_schedule_id?: string | null;
  membership_perk_plan_id?: string | null;
}

export interface PackageSessionOptionGroup<T> {
  kind: 'group';
  key: string;
  /** First option in the incoming (display) order; positions the tile. */
  lead: T;
  /** Sorted by session count; the first is the option shown by default. */
  options: T[];
}

export type PackageSessionOptionEntry<T> =
  | { kind: 'single'; pkg: T }
  | PackageSessionOptionGroup<T>;

/**
 * The display-group key, or null for packages that always stand alone.
 * Bundles fund sibling credits, fixed-slot packages pre-schedule sessions,
 * season/perk/internal packages are never sold from a list, per-player prices
 * would need a ladder per option, so none of them can share a tile.
 *
 * Besides name and coverage, the key holds every other attribute that
 * changes what the customer buys, so packages that merely share a name
 * (a first-time offer, another redemption window or date window) never merge.
 * It also holds the shared copy (description and features): a group save
 * writes one copy to every option, so options advertising different
 * inclusions must stay separate packages. Colour is per option and is not
 * part of it.
 */
export function getPackageSessionOptionKey(
  pkg: PackageSessionOptionKeySource,
): string | null {
  if (
    pkg.bundle_id ||
    pkg.is_fixed_slot ||
    pkg.class_schedule_id ||
    pkg.membership_perk_plan_id ||
    pkg.is_internal ||
    pkg.player_pricing
  ) {
    return null;
  }

  const name = pkg.name.trim().toLowerCase();

  if (!name) return null;

  return JSON.stringify([
    name,
    pkg.package_type,
    pkg.template_id ?? null,
    pkg.template_ids?.length ? [...pkg.template_ids].sort() : null,
    pkg.restrict_to_sport_id ?? null,
    pkg.location_id ?? null,
    pkg.tier ?? null,
    pkg.slot_duration ?? null,
    !!pkg.is_first_time_only,
    !!pkg.require_attendance_within_validity,
    pkg.fixed_starts_at ?? null,
    pkg.fixed_ends_at ?? null,
    pkg.status ?? null,
    normalizeCopy(pkg.description),
    normalizeCopy(pkg.description_alt),
    normalizeFeatures(pkg.features),
    normalizeFeatures(pkg.features_alt),
  ]);
}

/** Empty and missing copy are the same copy. */
function normalizeCopy(value: string | null | undefined) {
  return value?.trim() || null;
}

function normalizeFeatures(value: unknown) {
  if (!Array.isArray(value)) return [];
  return value
    .filter((feature): feature is string => typeof feature === 'string')
    .map((feature) => feature.trim())
    .filter(Boolean);
}

/**
 * A short stand-in for a key, for payloads sent to the browser: the full key
 * spells out template, sport and location ids. 53-bit, so two different keys
 * in one tenant's catalog colliding is negligible.
 */
export function hashPackageSessionOptionKey(key: string | null) {
  if (key === null) return null;

  let h1 = 0xdeadbeef;
  let h2 = 0x41c6ce57;
  for (let index = 0; index < key.length; index++) {
    const code = key.charCodeAt(index);
    h1 = Math.imul(h1 ^ code, 2654435761);
    h2 = Math.imul(h2 ^ code, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);

  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}

/**
 * Folds packages that share a key into one entry, positioned where its first
 * package appears (callers pass rows already in display order, so the first
 * one is the lead whose copy the tile shows). Options are sorted by session
 * count. A count offered twice under one key is ambiguous, so those rows stay
 * standalone; a key left with fewer than two options collapses to singles.
 */
export function groupPackageSessionOptions<T>(
  packages: readonly T[],
  keyOf: (pkg: T) => string | null | undefined,
  sessionCountOf: (pkg: T) => number,
): PackageSessionOptionEntry<T>[] {
  const membersByKey = new Map<string, T[]>();

  for (const pkg of packages) {
    const key = keyOf(pkg);

    if (!key) continue;

    const members = membersByKey.get(key);

    if (members) members.push(pkg);
    else membersByKey.set(key, [pkg]);
  }

  const optionsByKey = new Map<string, T[]>();

  for (const [key, members] of membersByKey) {
    const countFrequency = new Map<number, number>();

    for (const member of members) {
      const count = sessionCountOf(member);
      countFrequency.set(count, (countFrequency.get(count) ?? 0) + 1);
    }

    const options = members.filter(
      (member) => countFrequency.get(sessionCountOf(member)) === 1,
    );

    if (options.length >= 2) optionsByKey.set(key, options);
  }

  const entries: PackageSessionOptionEntry<T>[] = [];
  const emittedKeys = new Set<string>();

  for (const pkg of packages) {
    const key = keyOf(pkg);
    const options = key ? optionsByKey.get(key) : undefined;

    if (!key || !options || !options.includes(pkg)) {
      entries.push({ kind: 'single', pkg });
      continue;
    }

    if (emittedKeys.has(key)) continue;

    emittedKeys.add(key);
    entries.push({
      kind: 'group',
      key,
      lead: pkg,
      options: [...options].sort(
        (a, b) => sessionCountOf(a) - sessionCountOf(b),
      ),
    });
  }

  return entries;
}
